import { Box, Card, Paragraph, Tag } from '@zillow/constellation-10';
import { useAuth } from '../../AuthProvider';
import { useContext, useEffect, useRef, useState } from 'react';
import { LeadManagementContext } from '../../contexts/LeadManagementContextProvider';
import LatestContactMessage from '../LeadManagementTable/components/LatestContactMessage';
import useModal from '../../hooks/useModal';
import { ModalType } from '../../../src/types/ModalType';
import ScreeningCriteriaTooltip from '../LeadManagementTable/components/ScreeningCriteriaTooltip';
import NextStepTextButton from '../LeadManagementTable/components/NextStepTextButton';
import StatusLabel from '../StatusLabel';
import { emitOpenLeadProfileCardEvent } from '../../constants/clickstreamEvents/renterDetailsModal';
import useLeadManagementTableQuery, {
  getLeadManagementTableQueryKey,
  useCachedLeadByConversationId,
  useInvalidateLeadManagementTable,
} from '../../hooks/useLeadManagementTableQuery';
import useRealtimeUpdates, {
  LIST_MUTATING_EVENT_TYPES,
  useGuardedRealtimeUpdate,
} from '../../hooks/useRealtimeUpdates';
import LeadNameButton from '../LeadManagementTable/components/LeadNameButton';
import MoreActionsMenu from '../LeadManagementTable/components/MoreActionsMenu';
import getRenterDisplayName, { isNamelessPhoneLead } from '../../utils/getRenterDisplayName';
import StarIconButton from '../StarIconButton';
import LeadManagementCardsSkeleton from './Skeleton';
import LeadManagementModals from '../LeadManagementModals';
import { IsVisible } from '@zg-rentals/react-hooks';
import FolderStatusIcon from '../LeadManagementTable/components/FolderStatusIcon';
import ListingAddress from '../LeadManagementTable/components/ListingAddress';
import { useLoadMoreErrorToast } from '../../hooks/useLoadMoreErrorToast';
import getRenderableNextStepLink from '../../utils/getRenderableNextStepLink';

export default function LeadManagementCards() {
  const {
    clientTimeZone,
    conversationId: conversationIdFromUrl,
    conversationIdToFilterBy,
    folder,
    isArchivedFolder,
    isReportedFolder,
    listingAlias,
    listingAliases,
    messagingConversationId,
    minCriteriaMatches,
    readRepliedStatus,
    renterProgressStatus,
  } = useContext(LeadManagementContext);
  const { setActiveModal } = useModal();
  const { user } = useAuth();
  const [activeCardId, setActiveCardId] = useState('');
  const hasConversationIdFromUrl = !!conversationIdFromUrl;
  const openedLead = useCachedLeadByConversationId(conversationIdFromUrl || '');

  const queryParams = {
    clientTimeZone,
    conversationId: conversationIdToFilterBy,
    listingAliases,
    folder,
    minCriteriaMatches,
    readRepliedStatus,
    renterProgressStatus,
  };
  const { leads, hasNextPage, isLoading, isError, error, isFetchingNextPage, isFetchNextPageError, fetchNextPage } =
    useLeadManagementTableQuery(queryParams);

  const invalidateLeadManagementTable = useInvalidateLeadManagementTable();
  const handleRealtimeUpdate = useGuardedRealtimeUpdate(error, invalidateLeadManagementTable);

  useRealtimeUpdates({
    activeConversationId: messagingConversationId,
    clientTimeZone,
    onRealtimeUpdate: handleRealtimeUpdate,
    // subscribeToMessages delivers every frame from every subscribed topic, and one message fans
    // out into several event types — restrict to list-level types so a single received message
    // invalidates the table once, not once per frame. The open conversation's `newMessage` is
    // owned by useMessageListRealtimeUpdates.
    refetchOnEventTypes: LIST_MUTATING_EVENT_TYPES,
  });

  useLoadMoreErrorToast({
    isFetchNextPageError,
    isFetchingNextPage,
    hasNextPage,
    // Mirrors what useLeadManagementTableQuery keys on, so this changes exactly when the query
    // switches to a different cache entry (folder/filter change).
    queryKey: JSON.stringify(getLeadManagementTableQueryKey(queryParams)),
    onReset: invalidateLeadManagementTable,
  });

  useEffect(() => {
    if (conversationIdFromUrl && !isArchivedFolder && !isReportedFolder) {
      setActiveModal(ModalType.RenterDetailsModal);
      setActiveCardId(conversationIdFromUrl);
    } else if (conversationIdToFilterBy) {
      setActiveCardId(conversationIdToFilterBy);
    } else {
      setActiveCardId('');
    }
  }, [conversationIdFromUrl, conversationIdToFilterBy]);

  // Trigger a clickstream event only when the renter details modal is opened. Wait for the opened
  // lead to resolve (null on a cold deep link until the leads query lands) so the event carries
  // the row alias instead of the context fallback ('' unless exactly one listing is selected); the ref keeps it
  // once per open.
  const hasEmittedOpenLeadProfileRef = useRef(false);
  useEffect(() => {
    if (!hasConversationIdFromUrl) {
      hasEmittedOpenLeadProfileRef.current = false;
      return;
    }
    if (hasEmittedOpenLeadProfileRef.current || !openedLead) return;
    hasEmittedOpenLeadProfileRef.current = true;
    emitOpenLeadProfileCardEvent(
      user,
      { listingAlias: openedLead.listingDetails?.listingAlias ?? listingAlias },
      { leadId: openedLead.renterInfo?.leadId },
    );
  }, [hasConversationIdFromUrl, openedLead]);

  const isLoadingLeadsData = isLoading;

  if (isLoadingLeadsData) {
    return <LeadManagementCardsSkeleton />;
  }

  const handleFetchMoreLeads = (isVisible: boolean) => {
    if (!isVisible || isFetchingNextPage) return;
    fetchNextPage();
  };

  return (
    <>
      {activeCardId && <LeadManagementModals activeRowId={activeCardId} setActiveRowId={setActiveCardId} />}

      {leads.map(
        ({
          conversationId,
          latestContact,
          listingDetails,
          nextStep,
          renterInfo,
          screeningCriteria,
          status,
          statusLabel,
        }) => {
          const { leadId, renterName } = renterInfo;
          const { isNew, isPhoneLead } = latestContact;
          const { isConversationBlockedByZillow, isFavorite } = status;
          const renterNameDisplay = getRenterDisplayName(renterName, isPhoneLead, '');
          const isPhoneLeadLabel = isNamelessPhoneLead(renterName, isPhoneLead);
          const isActiveCard = conversationId === activeCardId;
          const statusLabelExists = !!statusLabel;

          return (
            <Card
              key={conversationId}
              data-testid="lead-row"
              data-conversation-id={conversationId}
              tone="neutral"
              css={{
                marginTop: 'default',
                paddingTop: 'tighter',
                backgroundColor: isActiveCard ? 'blue.100' : 'white',
                '& #lead-name': {
                  textDecoration: isActiveCard ? 'underline !important' : 'none',
                },
              }}
            >
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <LeadNameButton
                  conversationId={conversationId}
                  isPhoneLeadLabel={isPhoneLeadLabel}
                  leadName={renterNameDisplay}
                />
                <FolderStatusIcon />
                <Box css={{ display: 'flex', marginLeft: 'auto' }}>
                  <MoreActionsMenu
                    conversationId={conversationId}
                    iconColor="neutral"
                    setActiveRowId={setActiveCardId}
                  />
                  <Box css={{ marginLeft: 'tighter' }}>
                    <StarIconButton
                      conversationId={conversationId}
                      isConversationBlockedByZillow={isConversationBlockedByZillow}
                      isFavorite={isFavorite}
                    />
                  </Box>
                </Box>
              </Box>

              <Box css={{ display: 'flex', alignItems: 'center', marginBottom: 'default' }}>
                {isNew && (
                  <Tag tone="aqua" size="sm" css={{ marginRight: 'tighter', textTransform: 'uppercase' }}>
                    New
                  </Tag>
                )}

                {statusLabelExists ? (
                  <StatusLabel linkId={statusLabel.linkId} text={statusLabel.text} />
                ) : (
                  <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
                    Not available
                  </Paragraph>
                )}

                <Box css={{ marginLeft: 'default' }}>
                  <ScreeningCriteriaTooltip
                    conversationId={conversationId}
                    isPhoneLead={isPhoneLead}
                    leadId={leadId}
                    screeningCriteria={screeningCriteria}
                    setActiveRowId={setActiveCardId}
                    shouldDisplayRequestedText={false}
                  />
                </Box>
              </Box>

              <Box css={{ marginBottom: 'tighter' }}>
                <ListingAddress listingDetails={listingDetails} singleLine />
              </Box>

              <LatestContactMessage
                conversationId={conversationId}
                isConversationBlockedByZillow={isConversationBlockedByZillow}
                latestContactDetails={latestContact}
                leadId={leadId}
                shouldDisplayTimestampInline={true}
                listingAlias={listingDetails.listingAlias}
              />

              {getRenderableNextStepLink({ nextStep }) && (
                <Box css={{ marginTop: 'default' }}>
                  <NextStepTextButton conversationId={conversationId} setActiveRowId={setActiveCardId} />
                </Box>
              )}
            </Card>
          );
        },
      )}

      {hasNextPage && !isError && !isFetchNextPageError && (
        <IsVisible onChange={handleFetchMoreLeads}>
          <LeadManagementCardsSkeleton numberOfCards={2} />
        </IsVisible>
      )}

      <Paragraph css={{ textAlign: 'center', paddingTop: 'loose', paddingBottom: 'loose' }}>
        These are the leads you've received in the past 90 days.
      </Paragraph>
    </>
  );
}
